import CssBaseline from '@mui/material/CssBaseline';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { LicenseInfo } from '@mui/x-license';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useLocation } from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';

import type {} from '@mui/material/themeCssVarsAugmentation';
import { EnhancedRouter } from './components/EnhancedRouter';
import { Toaster } from './components/Toaster';
import { GTMProvider } from './contexts/GTMContext';
import { NativeBridgeProvider } from './contexts/NativeBridgeContext';
import { SessionContextProvider } from './contexts/SessionContext';
import { ThemeProvider } from './contexts/ThemeProvider';
import { useIntercomProps } from './hooks/useIntercomAppId';
import { usePostMessages } from './hooks/usePostMessages';
import Routes from './routes/Routes';
import { ErrorBoundary } from './shared/ErrorBoundary';

import './index.css';
import './i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false, staleTime: 5000 },
  },
});

const IntercomWrapper = ({ children }) => {
  const { appId, userId, userHash } = useIntercomProps();

  return (
    <IntercomProvider
      appId={appId || ''}
      autoBoot
      shouldInitialize={!!appId}
      autoBootProps={{
        userId,
        userHash,
      }}
    >
      {children}
    </IntercomProvider>
  );
};

const AppContent = () => (
  <IntercomWrapper>
    <GTMProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <HelmetProvider>
          <ThemeProvider>
            <ErrorBoundary>
              <Routes />
              <Toaster />
              <CssBaseline />
            </ErrorBoundary>
          </ThemeProvider>
        </HelmetProvider>
      </LocalizationProvider>
    </GTMProvider>
  </IntercomWrapper>
);

export function App() {
  usePostMessages();

  LicenseInfo.setLicenseKey(
    '5a9a7e16311c662d12497fbdf5aba0d4Tz0xMDE3NjAsRT0xNzYzMTM4MzU5MDAwLFM9cHJvLExNPXBlcnBldHVhbCxQVj1RMy0yMDI0LEtWPTI='
  );

  return (
    <QueryClientProvider client={queryClient}>
      <EnhancedRouter>
        <ScrollToTopOnNavigate />
        <NativeBridgeProvider>
          <SessionContextProvider>
            <AppContent />
          </SessionContextProvider>
        </NativeBridgeProvider>
      </EnhancedRouter>
    </QueryClientProvider>
  );
}
export const ScrollToTopOnNavigate = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};
